import React, { useEffect, useState, useContext } from 'react';
import Router from 'next/router';
import Auth from '@aws-amplify/auth';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  AlertIcon,
  Button,
  Center,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Flex,
  InputGroup,
  InputRightElement,
  Input,
  Spacer,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import withLogger, { Logger } from '../libs/logger';
import useToggle from '../hooks/useToggle';
import { EventStateContext } from '../context/EventContext';
import Storyblok from '../libs/storyblok';

type LoginFormProps = {
  callback(err: string | null, user?: { username: string; password: string }): void;
  log: Logger;
};

function LoginForm({ callback, log }: LoginFormProps) {
  const [error, setError] = useState('');
  const [showPwd, togglePwd] = useToggle(false);
  const { slug } = useContext(EventStateContext);
  const [showSupport, setShowSupport] = useState(false);

  const errorMessage = error.length > 0 && (
    <Alert status='error'>
      <AlertIcon />
      {error}
    </Alert>
  );

  // eslint-disable-next-line consistent-return
  async function login(evt: any) {
    setError('');

    const { email: username, password } = evt;
    try {
      const user = await Auth.signIn(`${username}#/${slug.slice(0, -1)}`, password);
      log.verbose({ action: 'login:resp', data: user });

      // Forced password reset on new account login
      if (user.challengeName) {
        return callback(user.challengeName, user);
      }

      return callback(null, user);
    } catch (err) {
      if (err.code) {
        if (err.code === 'UserNotConfirmedException') {
          return callback(err.code, { username, password });
        }
        if (err.code === 'PasswordResetRequiredException') {
          return callback(err.code, { username, password });
        }
      }

      log.error({ action: 'login:error', error: err });
      setError(err.message);
    }
  }

  async function onForgotPassword() {
    setError('');

    callback('FORGOT_PASSWORD');
  }

  async function onSignUp() {
    setError('');

    // Route to sign up
    await Router.push('/register').catch((e) => {
      log.error({ action: 'onSignUp:route:error', error: e });
      setError('Could not load register page');
    });
  }

  const loginSchema = yup.object({
    email: yup.string().email('Must be a valid email').required('Email is required'),
    password: yup.string().required('Password is required')
  });

  useEffect(() => {
    Storyblok.get(`cdn/stories/${slug.replace('conference-settings', '')}support`, {})
      .then(({ data }: any) => {
        setShowSupport(true);
      })
      .catch((err) => {
        setShowSupport(false);
        console.error('storyblok.get error', err, { name: err.name, message: err.message });
      });
  }, []);

  return (
    <Formik validationSchema={loginSchema} onSubmit={login} initialValues={{ email: '', password: '' }}>
      {({ isSubmitting, handleSubmit, handleChange, handleBlur, values, touched, errors }) => (
        <Form noValidate onSubmit={handleSubmit} color='gray.400'>
          {errorMessage}
          <Field name='email'>
            {({ field, form }) => (
              <FormControl mb='8' isInvalid={form.errors.email && form.touched.email}>
                <FormLabel htmlFor='email' srOnly>
                  Email
                </FormLabel>
                <Input
                  {...field}
                  id='email'
                  autoComplete='email'
                  placeholder='email'
                  bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                  colorScheme='whiteAlpha'
                />
                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
              </FormControl>
            )}
          </Field>

          <Field name='password'>
            {({ field, form }) => (
              <FormControl mb='8' isInvalid={form.errors.password && form.touched.password}>
                <FormLabel htmlFor='email' srOnly>
                  Password
                </FormLabel>
                <InputGroup size='md'>
                  <Input
                    {...field}
                    id='password'
                    placeholder='password'
                    autoComplete='password'
                    type={showPwd ? 'text' : 'password'}
                    bg={useColorModeValue('gray.300', 'whiteAlpha.400')}
                    colorScheme='whiteAlpha'
                  />
                  <InputRightElement width='4.5rem'>
                    <Button h='1.75rem' size='sm' onClick={togglePwd}>
                      {showPwd ? 'Hide' : 'Show'}
                    </Button>
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{form.errors.password}</FormErrorMessage>
              </FormControl>
            )}
          </Field>

          <Flex mb='8'>
            <Spacer />
            <Button variant='link' colorScheme='brand' onClick={onForgotPassword}>
              Forgot Password?
            </Button>
          </Flex>
          <Center>
            <Button
              borderRadius='4px'
              width='100%'
              type='submit'
              isLoading={isSubmitting}
              colorScheme='brand'
              size='lg'
            >
              Sign In
            </Button>
          </Center>
          {showSupport && (
            <Flex mt='8'>
              <Spacer />
              <Button
                variant='link'
                colorScheme='brand'
                onClick={() => {
                  Router.push(`${slug}support`);
                }}
              >
                Need Support?
              </Button>
            </Flex>
          )}
        </Form>
      )}
    </Formik>
  );
}

export default withLogger(LoginForm, 'component:LoginForm');
